import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader,
  Sparkles,
  Zap,
  Brain,
  CheckCircle,
  Play,
  RefreshCw,
  AlertTriangle,
  Code,
  FileText,
  Users
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import MermaidViewerWrapper from "@/components/MermaidViewerWrapper";
import ThinkingAnimation from "@/components/architecture/ThinkingAnimation";
import { useToast } from "@/components/ui/use-toast";
import axios from "axios";
import he from "he";
import { useError } from "@/contexts/ErrorProvider";
import { useParams } from "react-router-dom";
import DashboardLayout from "@/components/layouts/Dashboard";
import config from "@/config";
import {
  apiGetProjectDetails,
  apiValidateProjectSpecs,
  apiResumeProjectSpecs,
  ProjectDetailResponse
} from "@/services/ProjectService";
import EnhancedSubscriptionModalDark from "@/components/EnhancedSubscriptionModalDark.tsx";
import { useSubscriptionCheck } from "@/hooks/useSubscriptionCheck.ts";

const Preview: FC = () => {
  const { id } = useParams();
  
  const { toast } = useToast();
  
  const { setIsError } = useError();
  
  const { hasValidSubscription } = useSubscriptionCheck();
  
  const [refineEditor, setRefineEditor] = useState("");
  const [refine, setRefine] = useState("");
  const [mermaidDiagram, setMermaidDiagram] = useState("");
  const [disabledEdition, setDisabledEdition] = useState(false);
  const [status, setStatus] = useState("");
  const [isDemo, setIsDemo] = useState(false);
  const [microServicesShredCount, setMicroServicesShredCount] = useState<Record<string, number>>({});
  const [serviceUrls, setServiceUrls] = useState<Record<string, string>>({});
  const [diagramLoaded, setDiagramLoaded] = useState(false);
  const [hasLoadingDiv, setHasLoadingDiv] = useState(false);
  const [completedSections, setCompletedSections] = useState<string[]>([]);
  
  // New UX state management
  const [showRefineBlock, setShowRefineBlock] = useState(false);
  const [selectedService, setSelectedService] = useState<string>("");
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [showAllServices, setShowAllServices] = useState(false);
  
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | undefined>(
    undefined
  );
  
  const [loading, setLoading] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  
  const makeEditorDisabled = () => {
    const htmlEditor = document.querySelector(".public-DraftEditor-content");
    if (htmlEditor) {
      if (disabledEdition) {
        htmlEditor.setAttribute("contenteditable", disabledEdition.toString());
        setDisabledEdition(false);
      } else {
        htmlEditor.removeAttribute("contenteditable");
        setDisabledEdition(true);
      }
    }
  };
  
  const extractH1Sections = (htmlContent: string): string[] => {
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;
    
    // Extract all h1 tags
    const h1Elements = tempDiv.querySelectorAll("h1");
    const sections: string[] = [];
    
    h1Elements.forEach((h1) => {
      const text = h1.textContent?.trim();
      if (text) {
        sections.push(text);
      }
    });
    
    return sections;
  };
  
  const extractServiceUrlsFromDiagram = () => {
    const urls: Record<string, string> = {};
    const services: string[] = [];
    
    // Wait for Mermaid diagram to be fully rendered
    const checkForDiagram = () => {
      const svgElement = document.querySelector("svg[id*=\"mermaid\"]");
      if (!svgElement) return false;
      
      // Extract ALL service nodes (both clickable and non-clickable)
      const allNodes = svgElement.querySelectorAll(".node, .nodeLabel, foreignObject div, text");
      const clickableNodes = svgElement.querySelectorAll("a[data-node=\"true\"], a.clickable");
      
      // First, get clickable services with URLs (only store once per service)
      clickableNodes.forEach((node) => {
        const href = node.getAttribute("xlink:href") || node.getAttribute("href");
        const textElement = node.querySelector(".nodeLabel, foreignObject div, text");
        
        if (href && textElement) {
          let serviceName = textElement.textContent?.trim();
          
          if (serviceName && !urls[serviceName]) {
            // Only store the service once with its full name
            urls[serviceName] = href;
            if (!services.includes(serviceName)) {
              services.push(serviceName);
            }
          }
        }
      });
      
      // Then, extract ALL service names from the diagram (for development selection)
      allNodes.forEach((node) => {
        let serviceName = node.textContent?.trim();
        if (serviceName &&
          (serviceName.toLowerCase().includes("service") ||
            serviceName.toLowerCase().includes("api") ||
            serviceName.toLowerCase().includes("gateway") ||
            serviceName.toLowerCase().includes("database") ||
            serviceName.toLowerCase().includes("cache") ||
            serviceName.toLowerCase().includes("queue") ||
            serviceName.toLowerCase().includes("auth") ||
            serviceName.toLowerCase().includes("user") ||
            serviceName.toLowerCase().includes("order") ||
            serviceName.toLowerCase().includes("payment") ||
            serviceName.toLowerCase().includes("notification") ||
            serviceName.toLowerCase().includes("product") ||
            serviceName.toLowerCase().includes("inventory") ||
            serviceName.toLowerCase().includes("analytics") ||
            serviceName.toLowerCase().includes("search") ||
            serviceName.toLowerCase().includes("recommendation") ||
            serviceName.toLowerCase().includes("messaging") ||
            serviceName.toLowerCase().includes("storage") ||
            serviceName.toLowerCase().includes("processing")
          ) &&
          serviceName.length > 2 &&
          !services.includes(serviceName)) {
          services.push(serviceName);
        }
      });
      
      return services.length > 0;
    };
    
    // Try immediate extraction
    if (checkForDiagram()) {
      setServiceUrls(urls);
      setDiagramLoaded(true);
      return;
    }
    
    // If not found, use MutationObserver to wait for diagram to load
    const observer = new MutationObserver((mutations) => {
      let diagramFound = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          const addedNodes = Array.from(mutation.addedNodes);
          const hasRelevantNode = addedNodes.some(node =>
              node instanceof Element && (
                node.matches("svg[id*=\"mermaid\"]") ||
                node.querySelector("svg[id*=\"mermaid\"]") ||
                node.matches(".node, .nodeLabel") ||
                node.querySelector(".node, .nodeLabel")
              )
          );
          
          if (hasRelevantNode && checkForDiagram()) {
            diagramFound = true;
          }
        }
      });
      
      if (diagramFound) {
        setServiceUrls(urls);
        setDiagramLoaded(true);
        observer.disconnect();
      }
    });
    
    // Observe the entire document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Cleanup observer after 10 seconds to prevent memory leaks
    setTimeout(() => {
      observer.disconnect();
      setDiagramLoaded(true);
    }, 10000);
  };
  
  const getSpecs = () => {
    if (id !== undefined) {
      apiGetProjectDetails<ProjectDetailResponse>(id).then((response) => {
        if (response.data) {
          setIsError("");
          
          setStatus(response.data.step);
          setIsDemo(response.data.demo);
          
          if (response.data.specs != null) {
            setRefineEditor(response.data.specs);
            // Check if specs contain loading div
            setHasLoadingDiv(response.data.specs.includes("<div>Loading...</div>") || response.data.specs.includes("<div> Loading... </div>"));
            // Extract completed sections from H1 tags
            const sections = extractH1Sections(response.data.specs);
            setCompletedSections(sections);
          }
          
          if (response.data.diagram != null) {
            setMermaidDiagram(response.data.diagram);
          }
          
          if (response.data.microServicesShredCount != null) {
            setMicroServicesShredCount(response.data.microServicesShredCount);
          }
          
          makeEditorDisabled();
        }
      }).catch((errors: any) => {
        setIsError(errors?.response?.data?.message || errors.toString());
      });
    } else {
      setIsError("Project not found");
    }
  };
  
  const validateSpecs = () => {
    if (id !== undefined) {
      apiValidateProjectSpecs(id).then((response) => {
        if (response.data) {
          setLoading(false);
          window.location.reload();
        }
      }).catch((errors: any) => {
        setLoading(false);
        setIsError(errors?.response?.data?.message || errors.toString());
      });
    }
    setLoading(true);
  };
  
  const regenerate = async () => {
    if (refine.length < 5) {
      setError("Please fill all the required fields.");
      return;
    }
    try {
      const res = await axios
        .post(
          `${config.NEXT_PUBLIC_BASE_URL}/api/projects/${id}/specs/enhance`,
          {
            enhancements: refine
          },
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("accessToken")
            }
          }
        )
        .catch((err) => {
          setIsError(err.response.data.message);
          if (err.status === 400) {
            toast({
              title: "400",
              description: "Please fill all the required fields.",
              className: "bg-red-500 text-white"
            });
          }
          toast({
            title: "500",
            description: "Sorry something went wrong. Please try again later.",
            className: "bg-red-500 text-white"
          });
        });
      if (res && "data" in res) {
        setIsError("");
        window.location.reload();
      }
    } catch (error) {
      toast({
        title: "500",
        description: "Sorry something went wrong. Please try again later.",
        className: "bg-red-500 text-white"
      });
    }
  };
  
  const resume = (force: boolean) => {
    if (id !== undefined) {
      setResumed(true);
      apiResumeProjectSpecs<ProjectDetailResponse>(id, force).then((response) => {
        if (response.data) {
          setStatus(response.data.step);
          setIsDemo(response.data.demo);
          
          if (response.data.specs != null) {
            setRefineEditor(response.data.specs);
          }
          
          if (response.data.diagram != null) {
            setMermaidDiagram(response.data.diagram);
          }
          
          if (response.data.microServicesShredCount != null) {
            setMicroServicesShredCount(response.data.microServicesShredCount);
          }
          
          makeEditorDisabled();
        }
      }).catch((errors: any) => {
        setIsError(errors?.response?.data?.message || errors.toString());
      }).finally(() => {
        setResumed(false);
      });
    }
  };
  
  const redirectToUser = (serviceUrl: string) => {
    if (serviceUrl) {
      if (!hasValidSubscription()) {
        setShowSubscriptionModal(true);
      } else {
        window.location.href = serviceUrl;
      }
    }
  };
  useEffect(() => {
    getSpecs();
    
    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  
  // Extract service URLs when mermaid diagram changes
  useEffect(() => {
    if (mermaidDiagram) {
      // Reset state when new diagram loads
      setDiagramLoaded(false);
      setServiceUrls({});
      setSelectedService("");
      
      // Wait a bit for the MermaidViewerWrapper to render
      const timer = setTimeout(() => {
        extractServiceUrlsFromDiagram();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [mermaidDiagram]);
  
  useEffect(() => {
    if (status === "GENERATING") {
      const timer = setInterval(() => getSpecs(), 10000);
      setIntervalId(timer);
    }
    
    if (status !== "GENERATING") {
      window.clearInterval(intervalId);
    }
    
    return () => {
      if (intervalId)
        window.clearInterval(intervalId);
    };
  }, [status]);
  
  // Removed the early return - let it flow to the main layout
  
  return (
    <DashboardLayout>
      <div
        className="max-w-7xl m-auto gap-4 sm:gap-6 w-full flex flex-col grow justify-stretch mb-4 sm:mb-6 px-2 sm:px-0">
        {/* New Step-by-Step Progress Flow */}
        {status === "VALIDATED" && (
          <div
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-green-50 border-2 border-emerald-200 shadow-2xl">
            {/* Animated Background */}
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-emerald-200/20 to-green-200/20 rounded-full blur-2xl animate-pulse"></div>
              <div
                className="absolute -bottom-10 -left-10 w-32 h-32 bg-gradient-to-tr from-green-200/20 to-emerald-200/20 rounded-full blur-2xl animate-pulse delay-1000"></div>
            </div>
            
            <div className="relative p-8">
              {/* Step Indicator */}
              <div className="flex items-center justify-center mb-8">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <span className="text-sm font-medium text-emerald-700">Architecture Ready</span>
                  </div>
                  <div className="w-12 h-0.5 bg-emerald-300"></div>
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm animate-pulse">
                      2
                    </div>
                    <span className="text-sm font-medium text-blue-700">Select Service</span>
                  </div>
                  <div className="w-12 h-0.5 bg-gray-300"></div>
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-500 font-bold text-sm">
                      3
                    </div>
                    <span className="text-sm font-medium text-gray-500">Start Development</span>
                  </div>
                </div>
              </div>
              
              {/* Main Hero Section */}
              <div className="text-center mb-10">
                <div
                  className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl mb-6 shadow-xl">
                  <Code className="w-10 h-10 text-white" />
                </div>
                <h1
                  className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4">
                  Click a Service to Start Development
                </h1>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  Your architecture is ready! Click on any service below to start generating production-ready code.
                </p>
              </div>
              
              {/* Service Selection Grid - Only show clickable services */}
              {diagramLoaded && Object.keys(serviceUrls).length > 0 && (
                <div className="max-w-4xl mx-auto">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-blue-600" />
                      Available Services for Development
                    </h3>
                    <p className="text-gray-600">
                      <span
                        className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full text-xs font-semibold shadow-sm mr-2">
                        <Play className="w-3 h-3 mr-1" />
                        RESUME
                      </span>
                      Services with existing code appear first, then
                      <span
                        className="inline-flex items-center px-2.5 py-1 bg-gradient-to-r from-emerald-400 to-green-500 text-white rounded-full text-xs font-semibold shadow-sm mx-2">
                        <Sparkles className="w-3 h-3 mr-1" />
                        START
                      </span>
                      for new services. Click any service to begin development:
                    </p>
                  </div>
                  
                  {/* Services grid with show more functionality */}
                  <div className="relative mb-6">
                    {(() => {
                      const sortedServices = Object.keys(serviceUrls)
                        .filter(service => serviceUrls[service]) // Only services with URLs
                        .sort((a, b) => {
                          // Sort services with existing code first
                          const aHasCode = microServicesShredCount[a] > 0;
                          const bHasCode = microServicesShredCount[b] > 0;
                          if (aHasCode && !bHasCode) return -1;
                          if (!aHasCode && bHasCode) return 1;
                          return a.localeCompare(b); // Alphabetical for same priority
                        });
                      
                      const itemsPerRow = 4; // lg breakpoint shows 4 columns
                      const rowsToShow = 2;
                      const maxVisibleItems = itemsPerRow * rowsToShow;
                      const partialRowItems = Math.ceil(itemsPerRow / 2); // Show half of next row
                      const totalDisplayItems = maxVisibleItems + partialRowItems;
                      const shouldShowMore = sortedServices.length > totalDisplayItems;
                      const displayedServices = showAllServices ? sortedServices : sortedServices.slice(0, totalDisplayItems);
                      
                      return (
                        <>
                          <div className={`relative ${!showAllServices && shouldShowMore ? "pb-4" : ""}`}>
                            {/* Blur overlay for partial third row */}
                            {!showAllServices && shouldShowMore && (
                              <div
                                className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none z-10"></div>
                            )}
                            
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                              {displayedServices.map((service, index) => {
                                const serviceUrl = serviceUrls[service];
                                const hasExistingCode = microServicesShredCount[service] > 0;
                                const isPartialRow = !showAllServices && shouldShowMore && index >= maxVisibleItems;
                                
                                return (
                                  <div
                                    key={service}
                                    data-tour={index === 0 ? "validate-architcture" : undefined}
                                    className={`relative group cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                                      isPartialRow ? "opacity-60" : ""
                                    }`}
                                    onClick={() => {
                                      redirectToUser(serviceUrl);
                                    }}
                                  >
                                    <div
                                      className="relative p-3 rounded-xl border-2 transition-all duration-300 border-gray-200 bg-white hover:border-blue-300 hover:bg-gradient-to-br hover:from-blue-50 hover:to-indigo-50 hover:shadow-md">
                                      {/* Top row with action badge */}
                                      <div className="flex items-center justify-between mb-2">
                                        <div
                                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-300 shadow-sm ${
                                            hasExistingCode
                                              ? "bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-orange-200"
                                              : "bg-gradient-to-r from-emerald-400 to-green-500 text-white shadow-green-200"
                                          }`}
                                        >
                                          {hasExistingCode ? (
                                            <>
                                              <Play className="w-3 h-3 mr-1" />
                                              RESUME
                                            </>
                                          ) : (
                                            <>
                                              <Sparkles className="w-3 h-3 mr-1" />
                                              START
                                            </>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Service name */}
                                      <h4
                                        className="font-semibold text-gray-800 text-sm leading-tight group-hover:text-blue-700 transition-colors mb-1">
                                        {service}
                                      </h4>
                                      
                                      {/* Status text */}
                                      <p className="text-xs text-gray-500">
                                        {hasExistingCode ? "Ready to continue" : "Start fresh"}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          
                          </div>
                          
                          {/* Show More/Less Button */}
                          {shouldShowMore && (
                            <div className="relative z-20 text-center mt-4">
                              <Button
                                onClick={() => setShowAllServices(!showAllServices)}
                                variant="outline"
                                className="px-6 py-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl transition-colors"
                              >
                                {showAllServices ? (
                                  <>
                                    Show Less
                                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                            d="M5 15l7-7 7 7" />
                                    </svg>
                                  </>
                                ) : (
                                  <>
                                    Show More ({sortedServices.length - totalDisplayItems} more services)
                                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                            d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Secondary Actions (Hidden by Default) */}
        {(status === "GENERATED" || status === "VALIDATED") && !selectedService && (
          <div className="flex flex-col items-center gap-6">
            {/* Primary Actions */}
            {status === "GENERATED" && (
              <Button
                onClick={validateSpecs}
                loading={loading}
                className="group relative px-8 py-4 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold text-xl rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]"
              >
                <div className="relative flex items-center">
                  {loading ? (
                    <Loader className="w-6 h-6 animate-spin mr-4" />
                  ) : (
                    <Play className="w-6 h-6 mr-4" />
                  )}
                  <span>Go to Development</span>
                </div>
              </Button>
            )}
            
            {/* Secondary Actions */}
            <div className="flex flex-col items-center gap-3">
              {!showRefineBlock && (
                <Button
                  onClick={() => setShowRefineBlock(true)}
                  variant="outline"
                  className="px-6 py-2 border-gray-300 text-gray-600 hover:bg-gray-50 rounded-xl transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Modify Architecture
                </Button>
              )}
              
              {status === "GENERATED" && (
                <Button
                  onClick={() => setShowRegenerateConfirm(true)}
                  loading={resumed}
                  variant="outline"
                  className="px-4 py-2 border-orange-300 text-orange-600 hover:bg-orange-50 rounded-xl transition-colors text-sm"
                >
                  <RefreshCw className="w-3 h-3 mr-2" />
                  {resumed ? "Regenerating..." : "Regenerate Architecture"}
                </Button>
              )}
            </div>
          </div>
        )}
        
        {/* Architecture Refinement Section (Initially Hidden) */}
        {showRefineBlock && (
          <div
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-50 via-white to-purple-50 border border-indigo-100 shadow-xl">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-indigo-200/20 to-purple-200/20 rounded-full blur-2xl animate-pulse"></div>
              <div
                className="absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-to-tr from-blue-200/20 to-indigo-200/20 rounded-full blur-2xl animate-pulse delay-1000"></div>
            </div>
            
            <div className="relative p-4 sm:p-6 lg:p-8">
              {/* Close Button */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => setShowRefineBlock(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Header Section */}
              <div className="text-center mb-8">
                <div
                  className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-4 shadow-lg">
                  <Brain className="w-8 h-8 text-white" />
                </div>
                <h2
                  className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-800 bg-clip-text text-transparent mb-3">
                  Modify Your Architecture
                </h2>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
                  Describe what you'd like to change and let our AI enhance your architecture
                </p>
              </div>
              
              {/* Input Section */}
              <div className="max-w-4xl mx-auto">
                <div className="relative">
                  <div
                    className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl blur opacity-25"></div>
                  <div className="relative bg-white rounded-xl p-6 border border-indigo-100">
                    <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
                      <Sparkles className="w-4 h-4 mr-2 text-indigo-500" />
                      Describe your architectural changes
                    </label>
                    <Textarea
                      value={refine}
                      disabled={status === "GENERATING" || status === "FAILED"}
                      onChange={(e) => setRefine(e.target.value)}
                      className="w-full min-h-[120px] resize-none border-0 focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base leading-relaxed placeholder:text-gray-400"
                      placeholder="✨ Tell the AI what you want to change...

                                  Examples:
                                  • Add a Redis cache layer for better performance
                                  • Implement microservices pattern with API gateway
                                  • Include monitoring and logging services
                                  • Add database sharding for scalability"
                    />
                  </div>
                </div>
                
                {error && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <span className="text-sm text-red-600 font-medium flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd" />
                      </svg>
                      {error}
                    </span>
                  </div>
                )}
                
                {/* CTA Button */}
                <div className="flex justify-center mt-6">
                  <Button
                    disabled={status === "GENERATING" || status === "FAILED"}
                    className="group relative px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    onClick={regenerate}
                    loading={loading}
                  >
                    <div
                      className="absolute inset-0 bg-gradient-to-r from-indigo-700 to-purple-700 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative flex items-center">
                      {loading ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin mr-3" />
                          <span>AI is thinking...</span>
                        </>
                      ) : (
                        <>
                          <Zap className="w-5 h-5 mr-3 group-hover:animate-pulse" />
                          <span>Apply Changes</span>
                          <Sparkles className="w-4 h-4 ml-2 opacity-70" />
                        </>
                      )}
                    </div>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Enhanced Demo Section */}
        {isDemo && (
          <div
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 border border-violet-100 shadow-lg">
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-violet-200/20 to-fuchsia-200/20 rounded-full blur-xl animate-pulse"></div>
            </div>
            <div className="relative p-4 sm:p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div
                    className="w-12 h-12 bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-xl flex items-center justify-center">
                    <Play className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3
                    className="text-lg font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent mb-2">
                    Welcome to Demo Project
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Click on the{" "}
                    <code
                      className="px-3 py-1 bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white rounded-lg font-medium text-sm">
                      User Service
                    </code>{" "}
                    in the diagram below to access some generated code and explore the AI-powered development workflow.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Show ThinkingAnimation when GENERATING and no specs yet OR has loading div */}
        {status === "GENERATING" && (refineEditor === "" || hasLoadingDiv || !mermaidDiagram) && (
          <ThinkingAnimation
            hasLoadingDiv={hasLoadingDiv || refineEditor === ""}
            hasDiagram={!!mermaidDiagram}
            completedSections={completedSections}
          />
        )}
        
        {/* Enhanced Architecture Diagram Section - Only show when diagram exists */}
        {(status !== "FAILED") && mermaidDiagram && (
          <div
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50 via-white to-gray-50 border border-slate-200 shadow-lg">
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute -top-8 -left-8 w-32 h-32 bg-gradient-to-br from-slate-200/20 to-gray-200/20 rounded-full blur-2xl animate-pulse delay-500"></div>
            </div>
            <div className="relative">
              {/* Header */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100 bg-white/50 backdrop-blur-sm">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-10 h-10 bg-gradient-to-br from-slate-600 to-gray-700 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h2
                    className="text-xl font-semibold bg-gradient-to-r from-slate-700 to-gray-800 bg-clip-text text-transparent">
                    System Architecture Diagram
                  </h2>
                </div>
              </div>
              
              {/* Mobile/Safari warning banner */}
              {typeof window !== "undefined" && (
                isMobile ? (
                  <div
                    className="border-b border-slate-100 bg-gradient-to-r from-blue-100 to-indigo-100 border-blue-200 px-4 sm:px-6 py-4">
                    <div className="flex flex-col items-center text-center">
                      <div className="flex items-center justify-center mb-2">
                        <div
                          className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mr-3">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <span className="text-lg font-semibold text-blue-800">Better Experience Available</span>
                      </div>
                      <p className="text-sm text-blue-700 leading-relaxed mb-3">
                        <strong>Architecture diagrams work best on larger screens.</strong> For optimal viewing,
                        interaction, and navigation of the system diagram below, we recommend switching to:
                      </p>
                      <div className="flex flex-wrap justify-center gap-4 text-xs">
                        <div className="flex items-center bg-white bg-opacity-60 px-3 py-1.5 rounded-full">
                          <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor"
                               viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span className="font-medium text-blue-800">Desktop Computer</span>
                        </div>
                        <div className="flex items-center bg-white bg-opacity-60 px-3 py-1.5 rounded-full">
                          <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor"
                               viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span className="font-medium text-blue-800">Tablet (Landscape Mode)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  localStorage.getItem("safariWarningDismissed") !== "true" && /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && (
                    <div
                      className="border-b border-slate-100 bg-orange-100 dark:bg-orange-900 border-orange-200 dark:border-orange-700 px-4 sm:px-6 py-3">
                      <div className="flex items-center justify-center text-sm text-orange-800 dark:text-orange-200">
                        <span className="mr-2">⚠️</span>
                        <span>Safari detected - diagram display may have rendering issues, oversized shapes, or text positioning problems. For optimal diagram visualization, use Chrome or Firefox.</span>
                      </div>
                    </div>
                  )
                )
              )}
              
              {/* Services With Existing Code Section */}
              {Object.keys(microServicesShredCount).length > 0 && (
                <div
                  className="px-4 sm:px-6 py-3 border-b border-slate-100 bg-gradient-to-r from-green-50/50 to-emerald-50/50">
                  <div className="flex items-center space-x-2 mb-2">
                    <div
                      className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-green-600 rounded-md flex items-center justify-center">
                      <CheckCircle className="w-3 h-3 text-white" />
                    </div>
                    <h3
                      className="text-sm font-semibold bg-gradient-to-r from-emerald-600 to-green-700 bg-clip-text text-transparent">
                      Services With Existing Code
                    </h3>
                    {!diagramLoaded && (
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Loader className="w-3 h-3 animate-spin" />
                        <span>Loading URLs...</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(microServicesShredCount).map(([serviceName, count]) => {
                      const serviceUrl = serviceUrls[serviceName] || serviceUrls[serviceName.replace(/\s*Service\s*$/i, "").trim()];
                      const isClickable = diagramLoaded && serviceUrl;
                      const isLoading = !diagramLoaded;
                      
                      if (isClickable) {
                        return (
                          <a
                            key={serviceName}
                            href={serviceUrl}
                            className="inline-flex items-center space-x-2 px-3 py-1.5 bg-white border border-emerald-200 rounded-full text-xs font-medium shadow-sm hover:shadow-md hover:border-emerald-300 hover:bg-emerald-50 transition-all duration-200 cursor-pointer transform hover:scale-[1.02]"
                          >
                            <span className="text-gray-700">{serviceName}</span>
                            <div
                              className="w-5 h-5 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-xs">
                              {count}
                            </div>
                          </a>
                        );
                      }
                      
                      return (
                        <div
                          key={serviceName}
                          className={`inline-flex items-center space-x-2 px-3 py-1.5 bg-white border border-emerald-200 rounded-full text-xs font-medium shadow-sm transition-all duration-200 ${
                            isLoading ? "opacity-60 cursor-wait" : "opacity-50 cursor-not-allowed"
                          }`}
                        >
                          <span className="text-gray-500">{serviceName}</span>
                          <div
                            className="w-5 h-5 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                            {isLoading ? (
                              <Loader className="w-2.5 h-2.5 animate-spin" />
                            ) : (
                              count
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {diagramLoaded && Object.keys(serviceUrls).length === 0 && (
                    <div className="mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                      ⚠️ No clickable services found in diagram
                    </div>
                  )}
                </div>
              )}
              
              {/* Content */}
              <div className="p-4 sm:p-6">
                <MermaidViewerWrapper isDemo={isDemo} diagram={mermaidDiagram} onServiceClick={redirectToUser} />
              </div>
            </div>
          </div>
        )}
        
        {/* Enhanced Error Section */}
        {(status === "FAILED") && (
          <div
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-50 via-white to-rose-50 border border-red-100 shadow-lg">
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-red-200/20 to-rose-200/20now rounded-full blur-xl animate-pulse"></div>
            </div>
            <div className="relative flex flex-col gap-6 items-center justify-center py-16 px-8">
              <div
                className="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-lg">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
              <div className="text-center">
                <h3
                  className="text-2xl font-bold bg-gradient-to-r from-red-600 to-rose-700 bg-clip-text text-transparent mb-3">
                  Architecture Generation Failed
                </h3>
                <p className="text-gray-600 text-lg max-w-md">
                  An error occurred while generating the architecture. Don't worry, we can try again.
                </p>
              </div>
              <Button
                onClick={() => resume(false)}
                loading={resumed}
                className="group relative px-8 py-4 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-semibold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
              >
                <div className="relative flex items-center">
                  {resumed ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin mr-3" />
                      <span>Resuming...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5 mr-3" />
                      <span>Resume Generation</span>
                    </>
                  )}
                </div>
              </Button>
            </div>
          </div>
        )}
        
        {/* Enhanced Specifications Section - Show only when we have specs data */}
        {refineEditor && (
          <div
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 via-white to-indigo-50 border border-blue-100 shadow-lg">
            <div className="absolute inset-0 overflow-hidden">
              <div
                className="absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br from-blue-200/20 to-indigo-200/20 rounded-full blur-2xl animate-pulse delay-700"></div>
            </div>
            <div className="relative">
              {/* Header */}
              <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 border-b border-blue-100 bg-white/50 backdrop-blur-sm">
                <div className="flex items-center space-x-4">
                  <div
                    className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2
                      className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                      AI Generated Architecture Specifications
                    </h2>
                    <p className="text-gray-600 mt-1">
                      Detailed technical specifications created by our AI system
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4 sm:p-6 lg:p-8">
                <div
                  className="prose prose-sm sm:prose-base max-w-none prose-blue prose-headings:text-gray-800 prose-p:text-gray-700 prose-li:text-gray-700 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg">
                  <div
                    dangerouslySetInnerHTML={{ __html: he.decode(refineEditor.replace(/<div>Loading\.\.\.<\/div>/g, "").replace(/<div>\s*Loading\.\.\.\s*<\/div>/g, "")) }} />
                </div>
                
                {/* Bottom Action Button */}
                {status === "GENERATED" && (
                  <div className="mt-12 pt-8 border-t border-blue-100">
                    <div className="text-center">
                      <div className="mb-4">
                        <p className="text-lg text-gray-600 mb-2">
                          🚀 Ready to start building? Let's move to development!
                        </p>
                        <p className="text-sm text-gray-500">
                          Click below to proceed with service development
                        </p>
                      </div>
                      <Button
                        onClick={validateSpecs}
                        loading={loading}
                        className="group relative px-10 py-5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold text-xl rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]"
                      >
                        <div
                          className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-green-700 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative flex items-center">
                          {loading ? (
                            <>
                              <Loader className="w-6 h-6 animate-spin mr-4" />
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              <Play className="w-6 h-6 mr-4" />
                              <span>Go to Development</span>
                              <svg className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform duration-300"
                                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M13 7l5 5m0 0l-5 5m5-5H6" />
                              </svg>
                            </>
                          )}
                        </div>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Regenerate Confirmation Modal */}
        {showRegenerateConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-md w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Animated Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-red-50"></div>
              <div
                className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-br from-orange-200/30 to-red-200/30 rounded-full blur-xl animate-pulse"></div>
              
              <div className="relative p-6">
                {/* Warning Icon */}
                <div className="flex justify-center mb-4">
                  <div
                    className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center shadow-lg">
                    <AlertTriangle className="w-8 h-8 text-white" />
                  </div>
                </div>
                
                {/* Title */}
                <h3 className="text-xl font-bold text-gray-800 text-center mb-3">
                  Regenerate Architecture?
                </h3>
                
                {/* Warning Message */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <p className="text-gray-700 text-center leading-relaxed">
                    <span className="font-semibold text-orange-700">⚠️ Warning:</span> This will completely replace your
                    current architecture and specifications.
                  </p>
                  <div className="mt-3 space-y-2 text-sm text-gray-600">
                    <p className="flex items-center">
                      <span
                        className="w-4 h-4 bg-red-100 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      </span>
                      Current specifications will be deleted
                    </p>
                    <p className="flex items-center">
                      <span
                        className="w-4 h-4 bg-red-100 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      </span>
                      Architecture diagram will be recreated from scratch
                    </p>
                    <p className="flex items-center">
                      <span
                        className="w-4 h-4 bg-red-100 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                      </span>
                      AI will generate entirely new content
                    </p>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowRegenerateConfirm(false)}
                    variant="outline"
                    className="flex-1 py-3 border-gray-300 text-gray-600 hover:bg-gray-50 rounded-xl transition-colors"
                  >
                    <span>Keep Current Architecture</span>
                  </Button>
                  <Button
                    onClick={() => {
                      setShowRegenerateConfirm(false);
                      resume(true);
                    }}
                    loading={resumed}
                    className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {resumed ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin mr-2" />
                        <span>Regenerating...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        <span>Yes, Regenerate</span>
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Close button */}
                <button
                  onClick={() => setShowRegenerateConfirm(false)}
                  className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Enhanced Subscription Modal - Dark Theme (default for testing) */}
        <EnhancedSubscriptionModalDark
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
          projectId={id}
        />
        {/* Light theme version available: <EnhancedSubscriptionModal /> */}
      </div>
    </DashboardLayout>
  );
};

export default Preview;